import { useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';

import type {
  CalculateCustomPacingMetricsQuery,
  CalculateCustomPacingMetricsQueryVariables,
} from '../../../../../apis/graphql';
import { getErrorMessageFromGraphQlErrors } from '../../../../../apis/graphql';
import { showFailure } from '../../../../../common/utils';
import {
  ADJUST_SCHEDULE_NO_LINE_ITEM_FOUND,
  FAILURE_TITLE,
  NO_LINE_ITEMS_FOUND,
  UNEXPECTED_ERROR_MESSAGE,
} from '../constants';
import { CALCULATE_CUSTOM_PACING_METRICS } from '../queries';
import { CpScheduleFormFields } from '../types';
import { useScheduleFormState } from './useScheduleFormState';
import { getCalculationErrors, mapScheduleFormToCustomPacingInput } from './utils';

type UseCalculateReport = {
  calculate: () => Promise<void>;
  canCalculate: boolean;
  isCalculating: boolean;
  hasCalculatedReport: boolean;
  calculationError: string | null;
  hasLineItemsReports: boolean;
};

export const useCalculateReport = (lineItemIdList: string[]): UseCalculateReport => {
  const { values, isValid, resetForm, validateAndSetErrors } = useScheduleFormState();

  const [isCalculating, setIsCalculating] = useState(false);

  const { refetch } = useQuery<CalculateCustomPacingMetricsQuery, CalculateCustomPacingMetricsQueryVariables>(
    CALCULATE_CUSTOM_PACING_METRICS,
    { skip: true }
  );

  const setReportData = useCallback(
    (data: CalculateCustomPacingMetricsQuery['calculateCustomPacingMetrics']): void => {
      resetForm({
        values: {
          ...values,
          [CpScheduleFormFields.Summary]: data.summary,
          [CpScheduleFormFields.LineItemList]: data.lineItemList,
          [CpScheduleFormFields.ErrorList]: data.errorList,
        },
      });
    },
    [values, resetForm]
  );

  const calculate = useCallback(async (): Promise<void> => {
    if (!validateAndSetErrors()) return;

    setIsCalculating(true);

    const customPacing = mapScheduleFormToCustomPacingInput(values);

    try {
      const { data, errors } = await refetch({ lineItemIdList, customPacing });

      if (errors) {
        showFailure({
          message: FAILURE_TITLE,
          description: getErrorMessageFromGraphQlErrors(errors),
        });
        return;
      }

      if (!data || (!lineItemIdList.length && data.calculateCustomPacingMetrics.lineItemList.length === 0)) {
        showFailure({ message: NO_LINE_ITEMS_FOUND, description: ADJUST_SCHEDULE_NO_LINE_ITEM_FOUND });

        return;
      }

      setReportData(data.calculateCustomPacingMetrics);
    } catch {
      showFailure({
        message: FAILURE_TITLE,
        description: UNEXPECTED_ERROR_MESSAGE,
      });
    } finally {
      setIsCalculating(false);
    }
  }, [values, lineItemIdList, validateAndSetErrors, refetch, setReportData]);

  return {
    calculate,
    canCalculate: isValid,
    isCalculating,
    hasCalculatedReport: values.lineItemList.length > 0 || !!(values.summary && lineItemIdList.length),
    hasLineItemsReports: values.lineItemList.length > 0,
    calculationError: getCalculationErrors(values.lineItemList),
  };
};
